/* globals Map */
import React from 'react';
import ReactDOM from 'react-dom';

/**
 * @param {Element[]} containers
 * @param {Record<string, React.ComponentType>} components
 */
function renderComponents(containers, components) {
    containers.forEach(container => {
        if (container.dataset.renderedReactComponent) {
            return;
        }
        let props = tryParse(container.dataset.props);
        const component = components[container.dataset.reactComponent];
        if (component) {
            const element = React.createElement(component, props);
            ReactDOM.render(element, container);
            container.dataset.renderedReactComponent = true;
        }
    });
}

function tryParse(str) {
    return str ? JSON.parse(str) : undefined;
}

/**
 * Renders React components onto a page (client-side)
 * Pass in an object of the React components on the page to be rendered
 * In the HTML, use the [data-react-component] attribute on the element
 * you would like to render the component.
 * (e.g. data-react-component="MyComponent")
 * @param {Record<string, React.ComponentType>} components
 */
function renderReactClient(components) {
    if (typeof window !== 'undefined') {
        const containers = Array.from(document.querySelectorAll('[data-react-component]'));
        renderComponents(containers, components);
    }
}

/** @type {Map<string, React.ComponentType>} */
const componentRegistry = new Map();
const componentObserver = new MutationObserver(mutations => {
    const hasComponents = componentRegistry.size > 0;
    const shouldRender =
        hasComponents &&
        mutations.some(mutation => {
            return mutation.type === 'childList' && mutation.addedNodes.length > 0;
        });

    if (shouldRender) {
        renderComponents(
            Array.from(document.querySelectorAll('[data-react-component]')),
            Object.fromEntries(componentRegistry)
        );
    }
});
componentObserver.observe(document.body, { childList: true, subtree: true });

/**
 * Renders React components onto a page (client-side), and watches for new elements
 * with the `data-react-component` attribute to render components into.
 *
 * Pass in an object of the React components on the page to be rendered
 * In the HTML, use the [data-react-component] attribute on the element
 * you would like to render the component.
 * (e.g. `data-react-component="MyComponent"`)
 * @param {Record<string, React.ComponentType>} components
 */
export function autoRenderReactClient(components) {
    for (const [key, component] of Object.entries(components)) {
        componentRegistry.set(key, component);
    }

    if (typeof window !== 'undefined') {
        renderComponents(
            Array.from(document.querySelectorAll('[data-react-component]')),
            components
        );
    }
}

export default renderReactClient;
